<template>
    <div
        class="dt-milestones"
        :data-product-count="productCount"
        :data-product-id="shopifyProductId"
    >
        <h3>{{ title }}</h3>
        <p>{{ text }}</p>
        <div class="milestones-list">
            <div
                v-for="index in milestonesCount"
                :key="index"
                :data-index="index"
                class="milestone"
            >
                <div class="milestone-header">
                    <h4 class="milestone-header-title">
                        {{ milestoneLabel }} {{ index }}
                    </h4>
                    <button
                        v-if="index === milestonesCount && index !== 1"
                        class="delete"
                        @click="deleteMilestone(index)"
                    ></button>
                </div>
                <div class="milestone-input-wrapper" style="max-width: 124px">
                    <h5>{{ yearLabel }}</h5>
                    <DTInput
                        type="text"
                        :payload="`${index}.year`"
                        :placeholder="milestoneYearPlaceholder"
                        :required="index === 1 ? false : true"
                    />
                </div>
                <div class="milestone-input-wrapper">
                    <h5>{{ eventLabel }}</h5>
                    <DTInput
                        type="text"
                        :payload="`${index}.event`"
                        :placeholder="milestoneEventPlaceholder"
                        :autoHeight="true"
                        :required="index === 1 ? false : true"
                    />
                </div>
            </div>
        </div>
        <MessageBox
            id="milestonesMessagebox"
            type="success"
            :showIcon="false"
            :title="messageboxTitle"
            :message="messageboxText"
            margin="24px 0 0"
            :animation="false"
        />
        <MessageBox
            id="milestonesMessageboxMaxReached"
            type="success"
            :showIcon="false"
            :title="messageboxMaxReachedTitle"
            :message="messageboxMaxReachedText"
            margin="24px 0 0"
            :animation="false"
        />
        <DTButton
            id="milestonesAddButton"
            type="secondary"
            :icon="addButtonIcon"
            iconSize="16"
            :text="addButtonText"
            @click="addMilestone"
            :disabled="milestonesCount >= upperGate"
        />
    </div>
</template>

<script>
import DTInput from "./DTInput.vue";
import MessageBox from "./MessageBox.vue";
import DTButton from "./DTButton.vue";
import EventBus from "../event-bus";
import { safeGetJson, safeRemoveItem, safeSetItem, safeSetJson } from "../utils/safeStorage";

export default {
    name: "DTMilestones",
    props: {
        title: {
            type: String,
            default: "",
        },
        text: {
            type: String,
            default: "",
        },
        milestoneLabel: {
            type: String,
            default: "Meilenstein",
        },
        yearLabel: {
            type: String,
            default: "Jahr",
        },
        eventLabel: {
            type: String,
            default: "Ereignis",
        },
        milestoneYearPlaceholder: {
            type: String,
            default: "",
        },
        milestoneEventPlaceholder: {
            type: String,
            default: "",
        },
        lowerGate: {
            type: Number,
            default: 3,
        },
        upperGate: {
            type: Number,
            default: 12,
        },
        milestonesPerAdd: {
            type: Number,
            default: 3,
        },
        shopifyProductId: {
            type: String,
            default: "",
        },
        messageboxTitle: {
            type: String,
            default: "",
        },
        messageboxText: {
            type: String,
            default: "",
        },
        messageboxMaxReachedTitle: {
            type: String,
            default: "",
        },
        messageboxMaxReachedText: {
            type: String,
            default: "",
        },
        addButtonIcon: {
            type: String,
            default: "",
        },
        addButtonText: {
            type: String,
            default: "",
        },
    },
    components: {
        DTInput,
        MessageBox,
        DTButton,
    },
    data() {
        return {
            milestonesCount: 1,
            productCount: 0,
        };
    },
    inject: ["pageSettings"],
    mounted() {
        const stepValues = safeGetJson(this.pageSettings.storageKey, {});
        if (
            stepValues.milestones &&
            typeof stepValues.milestones === "object"
        ) {
            this.milestonesCount = Object.keys(stepValues.milestones).length;
        }

        if (
            this.milestonesCount === this.lowerGate ||
            this.milestonesCount % this.lowerGate === 0
        ) {
            EventBus.emit("showMessageBox", "milestonesMessagebox");
        }

        if (this.milestonesCount >= this.upperGate) {
            EventBus.emit("hideMessageBox", "milestonesMessagebox");
            EventBus.emit("showMessageBox", "milestonesMessageboxMaxReached");
        }

        this.setProductCount();
    },
    methods: {
        addMilestone() {
            if (this.milestonesCount >= this.upperGate) {
                return;
            }

            if (this.milestonesCount < this.lowerGate) {
                this.milestonesCount += 1;

                if (this.milestonesCount === this.lowerGate) {
                    EventBus.emit("showMessageBox", "milestonesMessagebox");
                }

                this.setProductCount();
                return;
            }

            if (this.milestonesCount % this.lowerGate === 0) {
                this.milestonesCount += this.milestonesPerAdd;
                EventBus.emit("showMessageBox", "milestonesMessagebox");
            } else {
                this.milestonesCount += 1;
                EventBus.emit("hideMessageBox", "milestonesMessagebox");
            }

            if (this.milestonesCount >= this.upperGate) {
                EventBus.emit("hideMessageBox", "milestonesMessagebox");
                EventBus.emit(
                    "showMessageBox",
                    "milestonesMessageboxMaxReached"
                );
            }

            this.setProductCount();
        },
        deleteMilestone(index) {
            if (index === 1) {
                return;
            }

            const milestone = document.querySelector(
                `.milestone[data-index="${index}"]`
            );

            milestone.style.opacity = 0;
            milestone.style.transform = "translateX(-20px)";

            setTimeout(() => {
                const stepValues = safeGetJson(this.pageSettings.storageKey, {});

                if (
                    stepValues.milestones &&
                    typeof stepValues.milestones === "object"
                ) {
                    delete stepValues.milestones[index];

                    safeSetJson(this.pageSettings.storageKey, stepValues);

                    this.milestonesCount = Object.keys(
                        stepValues.milestones
                    ).length;
                }

                if (
                    this.milestonesCount < this.lowerGate ||
                    this.milestonesCount % this.lowerGate !== 0
                ) {
                    EventBus.emit("hideMessageBox", "milestonesMessagebox");
                }

                EventBus.emit(
                    "hideMessageBox",
                    "milestonesMessageboxMaxReached"
                );

                this.setProductCount();
            }, 300);
        },

        setProductCount() {
            if (this.shopifyProductId && this.shopifyProductId.trim() !== '') {
                this.productCount = Math.ceil(
                    (this.milestonesCount - this.lowerGate) / this.milestonesPerAdd
                );
            } else {
                this.productCount = 0;
            }

            this.saveProductCountAndId();
        },
        saveProductCountAndId() {
            const milestonesProductCountKey =
                "milestonesProductCount_" + this.pageSettings.variant;
            const milestonesShopifyProductIdKey =
                "milestonesShopifyProductId_" + this.pageSettings.variant;

            safeSetItem(milestonesProductCountKey, this.productCount);
            
            if (this.shopifyProductId && this.shopifyProductId.trim() !== '') {
                safeSetItem(
                    milestonesShopifyProductIdKey,
                    this.shopifyProductId
                );
            } else {
                safeRemoveItem(milestonesShopifyProductIdKey);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.dt-milestones {
    > p {
        width: 100%;
        max-width: 515px;
        margin: 8px 0 0;

        & {
            @include font-body-s;
        }

        @media (max-width: $breakpoint-tablet) {
            margin: 16px 0 0;
        }

        @media (max-width: $breakpoint-mobile) {
            max-width: 100%;
        }
    }

    .milestones-list {
        display: flex;
        flex-direction: column;
        row-gap: 24px;
        margin-top: 24px;

        @media (max-width: $breakpoint-tablet) {
            row-gap: 32px;
            margin-top: 32px;
        }

        .milestone {
            position: relative;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            column-gap: 32px;
            transition: opacity 0.3s, transform 0.3s;

            @media (max-width: $breakpoint-mobile) {
                flex-direction: column;
                row-gap: 32px;
            }

            .milestone-header {
                position: absolute;
                top: 0;
                right: 0;

                @media (max-width: $breakpoint-mobile) {
                    position: unset;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    column-gap: 8px;
                    width: 100%;
                }

                .milestone-header-title {
                    display: none;

                    @media (max-width: $breakpoint-mobile) {
                        display: inline;
                    }
                }

                .delete {
                    width: 24px;
                    height: 24px;
                    border: none;
                    background-color: transparent;
                    background-image: url("~@/assets/customizer_icon_close.svg");
                    background-size: 11px;
                    background-repeat: no-repeat;
                    background-position: center center;
                    cursor: pointer;
                }
            }

            .milestone-input-wrapper {
                display: flex;
                flex-direction: column;
                row-gap: 16px;
                width: 100%;

                @media (max-width: $breakpoint-mobile) {
                    max-width: 100% !important;
                }
            }
        }
    }

    #milestonesAddButton {
        width: 100%;
        margin-top: 24px;

        &:disabled {
            filter: grayscale(1);
            cursor: not-allowed;
        }
    }
}
</style>
